const KICKOFF_LINK = 'https://calendly.com/nextlvlwebsites/kick-off-meeting';

export const PACKAGE_OPTIONS = [
  {
    id: 'starter',
    label: 'Starter',
    price: 997,
    timeline: '2 weeks',
    revisionLimit: 1,
    paymentPlan: '50/50',
    description: 'A clean 4-page website for businesses that need to look professional fast.',
  },
  {
    id: 'growth',
    label: 'Growth',
    price: 1997,
    timeline: '3-4 weeks',
    revisionLimit: 2,
    paymentPlan: '50/50',
    description: 'The primary offer: a custom lead-focused website for small businesses that want to look established.',
  },
  {
    id: 'authority',
    label: 'Authority',
    price: 3500,
    timeline: '3-5 weeks',
    revisionLimit: 2,
    paymentPlan: '40/40/20',
    description: 'A premium website with custom logic, lead funnels, booking, simple e-commerce, portals, or workflows.',
  },
  {
    id: 'custom',
    label: 'Custom',
    price: null,
    timeline: 'Scoped after discovery',
    revisionLimit: 2,
    paymentPlan: 'Custom',
    description: 'For projects that go beyond Authority and need a custom quote.',
  },
  {
    id: 'care-plan',
    label: 'Care Plan Only',
    price: 149,
    timeline: 'Ongoing',
    revisionLimit: 0,
    paymentPlan: 'Monthly',
    description: 'Ongoing support, monitoring, and edits for an existing site.',
  },
];

const addDays = (date, days) => {
  if (!date) return '';
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result.toISOString().split('T')[0];
};

const action = (name, link, locked = false) => ({
  name,
  link,
  complete: false,
  locked,
});

const baseProposal = ({
  projectName,
  service,
  investment,
  summary = '',
  scope = [],
  notIncluded = [],
  paymentSchedule = [],
  carePlanOption = null,
  serviceDetails,
  nextSteps,
}) => ({
  title: `${projectName} Proposal`,
  goals: '',
  service,
  investment,
  summary,
  scope,
  notIncluded,
  paymentSchedule,
  carePlanOption,
  serviceDetails,
  timeline: [],
  nextSteps,
});

const baseProject = ({ projectName, clientEmail, startDate, option, overrides }) => ({
  projectName,
  clientEmail,
  packageType: option.label,
  packageKey: option.id,
  packageDisplayName: option.label,
  price: option.price,
  paymentPlan: option.paymentPlan,
  revisionLimit: option.revisionLimit,
  revisionsUsed: 0,
        onboardingType: option.id === 'authority' ? 'authority-process-audit' : `${option.id}-intake`,
  carePlanOffered: false,
  carePlanStatus: 'not-offered',
  carePlanType: null,
  carePlanIncludedHours: 1,
  finalPaymentMade: false,
  currentStage: 0,
  creationDate: new Date(),
  ...overrides,
});

const supportResources = {
  trainingVideo: '/resources/training-video',
  faqs: '/client/faqs',
  guides: '/client/guides',
};

const launchNextSteps =
  "If you've reviewed the proposal and everything looks good, the next steps are to sign your contract and pay your deposit using the links provided below. The deposit reserves your project window and allows us to begin.";

const templates = {
  starter: ({ projectName, clientEmail, startDate }) => {
    const option = PACKAGE_OPTIONS.find((pkg) => pkg.id === 'starter');
    return baseProject({
      projectName,
      clientEmail,
      startDate,
      option,
      overrides: {
        track: 'brochure',
        keyDates: [
          { name: 'Project Start', date: startDate },
          { name: 'Assets Due', date: addDays(startDate, 2) },
          { name: 'Website Preview', date: addDays(startDate, 10) },
          { name: 'Launch Target', date: addDays(startDate, 14) },
        ],
        deliverables: [
          '4-page website: Home, About, Services, Contact',
          'Mobile-responsive design',
          'Template-based build with custom styling',
          'Contact form setup',
          'Google Analytics setup',
          'Basic on-page SEO',
          '30 days post-launch support',
        ],
        proposalDocuments: {
          proposal: baseProposal({
            projectName,
            service: 'Starter Website',
            investment: '997',
            summary: 'A focused 4-page website that gives your business a professional online presence, clear service information, and a simple way for customers to contact you.',
            scope: [
              'Home, About, Services, and Contact pages',
              'Template-based structure customized to match your business',
              'Mobile-responsive build with contact form setup',
              'Basic on-page SEO and Google Analytics setup',
            ],
            notIncluded: [
              'Custom app logic, portals, dashboards, or calculators',
              'CMS/blog setup, e-commerce, or more than 4 pages',
              'Ongoing edits after the 30-day support window unless a Care Plan is active',
            ],
            paymentSchedule: [
              '50% deposit due to reserve the project window: $498.50',
              '50% final payment due before launch: $498.50',
            ],
            carePlanOption: {
              title: 'Basic Care Plan',
              price: '$149/mo',
              description: 'Recommended after launch for backups, monitoring, priority support, and small monthly edits.',
            },
            serviceDetails: {
              website: ['Home page', 'About page', 'Services page', 'Contact page', 'Mobile-responsive build'],
              setup: ['Contact form', 'Google Analytics', 'Basic on-page SEO', 'Firebase hosting setup'],
              support: ['1 revision round', '30 days post-launch support', 'Care Plan option after launch'],
            },
            nextSteps: launchNextSteps,
          }),
          contract: '',
          invoice: '',
        },
        actions: [
          action('Approve Project and Pay Deposit', '/client/project-acceptance'),
          action('Complete Starter Intake', '/client/starter-intake'),
          action('Upload Logo and Photos', '/client/upload'),
          action('Submit Domain', '/client/domain'),
          action('Website Revision', '/client/website-revision', true),
          action('Launch', '/client/launch', true),
          action('Manage Care Plan Requests', '/client/care-plan-request', true),
        ],
        projectFiles: { finalFiles: '', analyticsAccess: '' },
        revisionDetails: { websiteRevision: { link: '', form: {} } },
        resources: supportResources,
        stages: [
          { name: 'Onboarding', start: startDate, end: addDays(startDate, 3) },
          { name: 'Build', start: addDays(startDate, 3), end: addDays(startDate, 10) },
          { name: 'Review', start: addDays(startDate, 10), end: addDays(startDate, 13) },
          { name: 'Launch', start: addDays(startDate, 14), end: addDays(startDate, 14) },
        ],
        status: 'Welcome to your Starter project. Start by approving the project, signing the contract, and paying the deposit. Then complete the intake and upload your logo/photos so the build can move fast.',
      },
    });
  },

  growth: ({ projectName, clientEmail, startDate }) => {
    const option = PACKAGE_OPTIONS.find((pkg) => pkg.id === 'growth');
    return baseProject({
      projectName,
      clientEmail,
      startDate,
      option,
      overrides: {
        track: 'brochure',
        keyDates: [
          { name: 'Kickoff Call', date: '' },
          { name: 'Project Start', date: startDate },
          { name: 'Messaging & Sitemap', date: addDays(startDate, 7) },
          { name: 'Homepage Preview', date: addDays(startDate, 14) },
          { name: 'Full Site Preview', date: addDays(startDate, 24) },
          { name: 'Launch Target', date: addDays(startDate, 28) },
        ],
        deliverables: [
          'Custom website up to 7 pages',
          'Homepage messaging guidance',
          'Service page structure',
          'Mobile-responsive design',
          'Local SEO optimization',
          'Domain and hosting setup',
          'Google Analytics + Search Console',
          '60 days post-launch support',
        ],
        proposalDocuments: {
          proposal: baseProposal({
            projectName,
            service: 'Growth Website',
            investment: '1997',
            summary: 'A custom lead-focused website built to make the business look established, explain the offer clearly, and turn local visitors into inquiries.',
            scope: [
              'Custom website up to 7 pages',
              'Homepage messaging guidance and page structure',
              'Local SEO foundation for service-area visibility',
              'Domain, hosting, Google Analytics, and Search Console setup',
            ],
            notIncluded: [
              'Custom portals, dashboards, calculators, or app-style workflows',
              'Large e-commerce builds or advanced third-party integrations',
              'Unlimited revisions, ad management, or ongoing support after 60 days unless a Care Plan is active',
            ],
            paymentSchedule: [
              '50% deposit due to reserve the project window: $998.50',
              '50% final payment due before launch: $998.50',
            ],
            carePlanOption: {
              title: 'Basic Care Plan',
              price: '$149/mo',
              description: 'Recommended after launch so content updates, backups, monitoring, and priority support stay handled.',
            },
            serviceDetails: {
              strategy: ['Kickoff call', 'Messaging guidance', 'Sitemap and page structure', 'Local SEO direction'],
              website: ['Custom homepage', 'Up to 7 pages', 'Service page structure', 'Mobile-responsive development'],
              launch: ['Domain and hosting setup', 'Google Analytics + Search Console', '2 revision rounds', '60 days post-launch support'],
            },
            nextSteps: launchNextSteps,
          }),
          contract: '',
          invoice: '',
        },
        actions: [
          action('Approve Project and Pay Deposit', '/client/project-acceptance'),
          action('Schedule Kick-off Call', KICKOFF_LINK),
          action('Complete Growth Intake', '/client/growth-intake'),
          action('Upload Images', '/client/upload'),
          action('Custom Domain Set Up', '/client/domain'),
          action('Home Page Revision', '/client/homepage-revision', true),
          action('Website Revision', '/client/website-revision', true),
          action('Launch', '/client/launch', true),
          action('Manage Care Plan Requests', '/client/care-plan-request', true),
        ],
        projectFiles: { brandStrategy: '', sitemap: '', firebaseProject: '', finalFiles: '' },
        revisionDetails: {
          homePageRevision: { link: '', form: {} },
          websiteRevision: { link: '', form: {} },
        },
        resources: supportResources,
        clientPortalTutorialVideo: '',
        stages: [
          { name: 'Onboarding', start: startDate, end: addDays(startDate, 5) },
          { name: 'Messaging & Sitemap', start: addDays(startDate, 5), end: addDays(startDate, 8) },
          { name: 'Homepage Design', start: addDays(startDate, 8), end: addDays(startDate, 14) },
          { name: 'Full Site Build', start: addDays(startDate, 14), end: addDays(startDate, 23) },
          { name: 'Revision Round 1', start: addDays(startDate, 23), end: addDays(startDate, 25) },
          { name: 'Revision Round 2', start: addDays(startDate, 25), end: addDays(startDate, 27) },
          { name: 'Launch', start: addDays(startDate, 28), end: addDays(startDate, 28) },
          { name: 'Care Plan Setup', start: addDays(startDate, 29), end: addDays(startDate, 30) },
        ],
        status: 'Welcome to your Growth project. Start by approving the project, signing the contract, and paying the deposit. Then schedule the kickoff call and complete the intake so the messaging and sitemap can be shaped correctly.',
      },
    });
  },

  authority: ({ projectName, clientEmail, startDate }) => {
    const option = PACKAGE_OPTIONS.find((pkg) => pkg.id === 'authority');
    return baseProject({
      projectName,
      clientEmail,
      startDate,
      option,
      overrides: {
        track: 'custom-logic',
        keyDates: [
          { name: 'Process Audit', date: '' },
          { name: 'Project Start', date: startDate },
          { name: 'Scope Lock', date: addDays(startDate, 5) },
          { name: 'Workflow Map Approval', date: addDays(startDate, 9) },
          { name: 'Core Build Preview', date: addDays(startDate, 21) },
          { name: 'Testing Window', date: addDays(startDate, 28) },
          { name: 'Launch Target', date: addDays(startDate, 35) },
        ],
        deliverables: [
          'Custom website up to 12 pages',
          'Lead capture or funnel setup',
          'Booking, calculator, portal, custom form logic, or scoped custom workflow',
          'Blog/CMS setup where needed',
          'Small e-commerce setup where scoped',
          'Speed and performance optimization',
          'Testing and QA window',
          '90 days post-launch support',
        ],
        proposalDocuments: {
          proposal: baseProposal({
            projectName,
            service: 'Authority Website',
            investment: '3500',
            summary: 'A premium website with scoped custom functionality for businesses that need more than a brochure site, such as lead funnels, booking flows, calculators, portals, or workflow tools.',
            scope: [
              'Custom website up to 12 pages',
              'Process audit, workflow map, and scope lock before build',
              'Scoped custom functionality such as booking, calculator, portal, funnel, or custom form logic',
              'Testing window, performance optimization, launch support, and 90 days post-launch support',
            ],
            notIncluded: [
              'Unscoped custom features or feature changes after scope lock',
              'Large inventory e-commerce, marketplace systems, subscription platforms, or complex multi-role SaaS apps',
              'Third-party integrations that require paid accounts, approvals, or undocumented API work unless scoped separately',
              'Ongoing development after the 90-day support window unless a Care Plan or change order is active',
            ],
            paymentSchedule: [
              '40% deposit due to reserve the project window: $1,400',
              '40% milestone payment due after design/functionality approval: $1,400',
              '20% final payment due before launch: $700',
            ],
            carePlanOption: {
              title: 'Plus Care Plan',
              price: '$199/mo',
              description: 'Recommended for Authority builds so custom functionality, monitoring, backups, priority support, and monthly improvements stay maintained.',
            },
            serviceDetails: {
              strategy: ['Process audit', 'Scope lock', 'Workflow map', 'Custom functionality requirements'],
              website: ['Custom website up to 12 pages', 'Lead funnel setup', 'CMS/blog setup if needed', 'Performance optimization'],
              functionality: ['Booking/calculator/portal/custom form logic as scoped', 'Testing and QA', 'Admin/customer workflow review'],
              launch: ['Priority launch process', '2 revision rounds', '90 days post-launch support', 'Care Plan Plus recommendation'],
            },
            nextSteps: "If everything looks good, please sign the contract and pay the first 40% deposit. Because this project includes custom functionality, the scope will be locked before build work begins. Anything outside that scope can be added through a change order.",
          }),
          contract: '',
          invoice: '',
        },
        actions: [
          action('Approve Project and Pay Deposit', '/client/project-acceptance'),
          action('Schedule Process Audit', KICKOFF_LINK),
          action('Complete Process Audit', '/client/process-audit'),
          action('Complete Brand Questionnaire', '/client/brand-questionnaire'),
          action('Provide Website Content', '/client/web-content'),
          action('Upload Images and Assets', '/client/upload'),
          action('Custom Domain Set Up', '/client/domain'),
          action('Home Page Revision', '/client/homepage-revision', true),
          action('Website Pages Revision', '/client/website-revision', true),
          action('Client Portal Revision', '/client/client-portal-revision', true),
          action('Admin Portal Revision', '/client/admin-portal-revision', true),
          action('Testing & QA', '/client/testing-qa', true),
          action('Launch', '/client/launch', true),
          action('Manage Care Plan Requests', '/client/care-plan-request', true),
        ],
        projectFiles: { requirementsDoc: '', workflowMap: '', firebaseProject: '', finalFiles: '' },
        revisionDetails: {
          homePageRevision: { link: '', form: {} },
          websiteRevision: { link: '', form: {} },
          clientPortalRevision: { link: '', form: {} },
          adminPortalRevision: { link: '', form: {} },
          testingQA: { link: '', form: {} },
        },
        resources: { ...supportResources, adminTutorial: '/resources/admin/tutorial', clientTutorial: '/resources/client/tutorial' },
        scope: { included: [], excluded: [], openDecisions: [], changeOrders: [] },
        stages: [
          { name: 'Process Audit', start: startDate, end: addDays(startDate, 3) },
          { name: 'Scope Lock', start: addDays(startDate, 3), end: addDays(startDate, 6) },
          { name: 'Workflow Map', start: addDays(startDate, 6), end: addDays(startDate, 10) },
          { name: 'Design Direction', start: addDays(startDate, 10), end: addDays(startDate, 14) },
          { name: 'Website Build', start: addDays(startDate, 14), end: addDays(startDate, 21) },
          { name: 'Custom Functionality Build', start: addDays(startDate, 21), end: addDays(startDate, 28) },
          { name: 'Internal Testing', start: addDays(startDate, 28), end: addDays(startDate, 31) },
          { name: 'Client Testing', start: addDays(startDate, 31), end: addDays(startDate, 34) },
          { name: 'Revisions / Bug Fixes', start: addDays(startDate, 34), end: addDays(startDate, 36) },
          { name: 'Launch', start: addDays(startDate, 37), end: addDays(startDate, 37) },
          { name: 'Care Plan Plus Setup', start: addDays(startDate, 38), end: addDays(startDate, 39) },
        ],
        status: 'Welcome to your Authority project. Start by approving the project, signing the contract, and paying the first deposit. Then schedule the process audit so we can lock scope and map the custom workflow before build work begins.',
      },
    });
  },

  custom: ({ projectName, clientEmail, startDate }) => {
    const authorityProject = templates.authority({ projectName, clientEmail, startDate });
    return {
      ...authorityProject,
      packageType: 'Custom',
      packageKey: 'custom',
      packageDisplayName: 'Custom',
      price: null,
      paymentPlan: 'Custom',
      status: 'Welcome to your custom project. Start with discovery so the scope, price, and timeline can be finalized.',
      proposalDocuments: {
        ...authorityProject.proposalDocuments,
        proposal: {
          ...authorityProject.proposalDocuments.proposal,
          service: 'Custom Website / Web App',
          investment: '',
          summary: 'A custom-scoped website or web app for work that goes beyond the standard Authority package.',
          paymentSchedule: ['Payment schedule will be finalized after discovery and scope approval.'],
        },
      },
    };
  },

  'care-plan': ({ projectName, clientEmail, startDate }) => {
    const option = PACKAGE_OPTIONS.find((pkg) => pkg.id === 'care-plan');
    return baseProject({
      projectName,
      clientEmail,
      startDate,
      option,
      overrides: {
        track: 'care-plan',
        carePlanOffered: true,
        carePlanStatus: 'pending-subscription',
        carePlanType: 'basic',
        carePlanIncludedHours: 1,
        keyDates: [
          { name: 'Care Plan Start', date: startDate },
          { name: 'First Monthly Review', date: addDays(startDate, 30) },
        ],
        deliverables: [
          'Monthly backups',
          'Uptime monitoring',
          'Priority email support',
          'Up to 1 hour of edits per month',
          'Small fixes and content updates',
        ],
        proposalDocuments: {
          proposal: baseProposal({
            projectName,
            service: 'Monthly Care Plan',
            investment: '149/mo',
            summary: 'Ongoing website support for businesses that want updates, monitoring, backups, and small fixes handled without managing the technical side themselves.',
            scope: [
              'Monthly backups and uptime monitoring',
              'Priority email support',
              'Up to 1 hour of small edits per month',
              'Small fixes, content updates, and support notes tracked through the portal',
            ],
            notIncluded: [
              'Unused edit time does not roll over unless approved in writing',
              'New pages, redesigns, custom functionality, or major feature work',
              'Paid tools, hosting upgrades, third-party subscriptions, or ad management',
            ],
            paymentSchedule: ['Monthly subscription billed at the start of each service month.'],
            carePlanOption: {
              title: 'Basic Care Plan',
              price: '$149/mo',
              description: 'Ongoing support for simple websites: backups, uptime monitoring, priority email support, and up to 1 hour of small edits per month.',
            },
            serviceDetails: {
              care: ['Backups', 'Uptime monitoring', 'Priority email support', 'Up to 1 hour of edits per month'],
            },
            nextSteps: 'Once the subscription is active, send website update requests through the client portal.',
          }),
          contract: '',
          invoice: '',
        },
        actions: [
          action('Activate Care Plan Subscription', '/client/project-acceptance'),
          action('Manage Website Update Requests', '/client/care-plan-request', true),
        ],
        projectFiles: { websiteURL: '', supportNotes: '' },
        revisionDetails: {},
        resources: supportResources,
        stages: [
          { name: 'Active Support', start: startDate, end: addDays(startDate, 30) },
          { name: 'Monthly Review', start: addDays(startDate, 30), end: addDays(startDate, 30) },
        ],
        status: 'Your Care Plan is pending subscription activation. Once the subscription is confirmed, you can submit website update requests through the portal.',
      },
    });
  },
};

export const getPackageOption = (packageId) =>
  PACKAGE_OPTIONS.find((option) => option.id === packageId) || PACKAGE_OPTIONS[0];

export const createProjectFromTemplate = ({ packageId, projectName, clientEmail, startDate }) => {
  const build = templates[packageId] || templates.starter;
  return build({ projectName, clientEmail, startDate });
};
